/**
 * Pure helpers for choosing the run dialog's initial environment.
 *
 * Extracted from WorkflowRunConfigModal so the selection precedence is unit
 * testable without pulling in React/UI.
 *
 * @module FlowDesigner/components/environmentSelection
 */

export interface EnvironmentOption {
  id: string;
  name: string;
  type: string;
  isDefault: boolean;
  environmentKindId?: string | null;
  environmentKindName?: string | null;
}

export interface EnvironmentStageOption {
  id: string;
  name: string;
}

/**
 * Pick the environment to pre-select in the run dialog.
 *
 * Precedence:
 *   1. `preferredId` — the login credential's bound env (server-derived) when it
 *      is a real, eligible env. Lets a Sales-bound credential default to "Sales".
 *   2. The property default (`isDefault`) env, if eligible.
 *   3. The first eligible env.
 */
export function pickInitialEnvironmentId(
  environments: EnvironmentOption[] | undefined,
  eligibleIds: string[] | undefined,
  preferredId?: string | null
): string | undefined {
  if (!environments || environments.length === 0) return undefined;
  const eligible = new Set(eligibleIds ?? environments.map((e) => e.id));
  if (preferredId && eligible.has(preferredId) && environments.some((e) => e.id === preferredId)) {
    return preferredId;
  }
  const defaultEnv = environments.find((e) => e.isDefault && eligible.has(e.id));
  if (defaultEnv) return defaultEnv.id;
  const firstEligible = environments.find((e) => eligible.has(e.id));
  return firstEligible?.id;
}

/** Pick the org stage represented by the preferred/default application environment. */
export function pickInitialEnvironmentStageId(
  environments: EnvironmentOption[] | undefined,
  stages: EnvironmentStageOption[] | undefined,
  preferredEnvironmentId?: string | null
): string | undefined {
  if (!stages || stages.length === 0) return undefined;
  const stageIds = new Set(stages.map((stage) => stage.id));
  const preferredStageId = preferredEnvironmentId
    ? environments?.find((environment) => environment.id === preferredEnvironmentId)
        ?.environmentKindId
    : null;
  if (preferredStageId && stageIds.has(preferredStageId)) return preferredStageId;

  const defaultStageId = environments?.find(
    (environment) => environment.isDefault && environment.environmentKindId
  )?.environmentKindId;
  if (defaultStageId && stageIds.has(defaultStageId)) return defaultStageId;

  const representedStageId = environments?.find(
    (environment) => environment.environmentKindId && stageIds.has(environment.environmentKindId)
  )?.environmentKindId;
  return representedStageId ?? stages[0]?.id;
}
